import React, { useState } from "react";
import { useAtom } from "jotai";
import {
    Grid,
    Button,
    Typography,
    TablePagination,
    IconButton,
    TextField,
    InputAdornment,
    Radio,
    RadioGroup,
    Menu,
    Stack,
    Box,
    FormControl,
    FormLabel,
    FormControlLabel,
    Link,
    LinearProgress
} from "@mui/material";
import { AddEditServiceDialog } from "../AddEditServiceDialog.js";
import { AddEditCategoriesDialog } from "../AddEditCategoriesDialog.js";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { useQuery } from "@tanstack/react-query";
import { ServiceTable } from "./ServiceTable.js";
import { execReadBySortkey } from "../../modules/DBService";
import * as queries from "../../graphql/queries.js";
import {
    tableConfigAtom,
    selectedServiceIdAtom,
    openServiceDialogAtom,
    visibleRows,
    visibleRowsPaginated,
    paginationAtom,
    setPageAtom,
    setPerPageAtom,
    STATUS_OPTIONS,
    TYPE_OPTIONS,
    BOOKING_BEHAVIOUR_OPTIONS
} from "../../atoms/servicesTable.js";
import {
    mbxUserAtom,
    allServicesAtom,
    isLoadingServicesAtom,
    loadingServicesProgressAtom,
    isMbxUserVerifiedStaff
} from "../../atoms/atoms";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import {
    tableTheme,
    tableConfigTheme,
    dropDownMenuTheme,
    paginationTheme
} from "../../styles/ListViewTheme.js";

const STATUS_OPTIONS_LABELS = {
    [STATUS_OPTIONS.ALL]: "All",
    [STATUS_OPTIONS.ACTIVE]: "Active",
    [STATUS_OPTIONS.INACTIVE]: "Inactive"
};

const TYPE_OPTIONS_LABELS = {
    [TYPE_OPTIONS.ALL]: "All",
    [TYPE_OPTIONS.SIMPLE]: "One-on-one",
    [TYPE_OPTIONS.GROUP]: "Group"
};

const BOOKING_BEHAVIOUR_OPTIONS_LABELS = {
    [BOOKING_BEHAVIOUR_OPTIONS.ALL]: "All",
    [BOOKING_BEHAVIOUR_OPTIONS.PRIVATE]: "Private",
    [BOOKING_BEHAVIOUR_OPTIONS.PUBLIC]: "Public",
    [BOOKING_BEHAVIOUR_OPTIONS.WAITLIST]: "Waitlist"
};

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
export const ServicesPage = () => {
    const [mbxUser] = useAtom(mbxUserAtom);

    const [loadingServicesProgress] = useAtom(loadingServicesProgressAtom);
    const [, setIsLoadingServices] = useAtom(isLoadingServicesAtom);
    const [displayServices] = useAtom(visibleRowsPaginated);
    const [allViewableServices] = useAtom(visibleRows);
    const [, setAllServices] = useAtom(allServicesAtom);

    const [tableConfig] = useAtom(tableConfigAtom);
    const { statusFilter } = tableConfig;

    const [paginationConfig] = useAtom(paginationAtom);
    const [, setPerPageAction] = useAtom(setPerPageAtom);
    const [, setPageAction] = useAtom(setPageAtom);
    const { page, perPage } = paginationConfig;

    const theme = useTheme();

    const { isLoading: isLoadingServices } = useQuery({
        queryKey: ["get-services", statusFilter],
        queryFn: async () => {
            try {
                // Dynamically adjust the filter based on the statusFilter value
                const filter = {
                    deleted: { ne: true },
                    isBundledService: { ne: true },
                    ...(statusFilter !== STATUS_OPTIONS.ALL && {
                        active: { eq: statusFilter === STATUS_OPTIONS.ACTIVE }
                    })
                };
                let { items, errors } = await execReadBySortkey({
                    opname: "serviceTypeByCompany",
                    op: queries.serviceTypeByCompany,
                    id: {
                        companyId: mbxUser.companyId
                    },
                    filter,
                    limit: 500
                });
                if (errors) throw errors;
                return items;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (schedulesData) => {
            setAllServices(schedulesData);
        }
    });

    setIsLoadingServices(isLoadingServices);

    const handlePageChange = (event, newPage) => {
        setPageAction(newPage);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <>
            {isLoadingServices ? (
                <Box sx={{ width: "100%", paddingTop: "18px" }}>
                    <LinearProgress
                        variant="buffer"
                        valueBuffer={loadingServicesProgress}
                        value={loadingServicesProgress}
                    />
                </Box>
            ) : (
                <ThemeProvider theme={tableTheme(theme)}>
                    <TableHeader />
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column"
                        }}
                    >
                        {displayServices.length === 0 ? (
                            <>
                                <ServiceTable services={[]} />
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: "70vh"
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        color={theme.palette.text.secondary}
                                    >
                                        No services found
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            <ServiceTable services={displayServices} />
                        )}
                    </Box>
                    <ThemeProvider theme={paginationTheme(theme)}>
                        <TablePagination
                            count={allViewableServices?.length || 0}
                            component="div"
                            page={page}
                            rowsPerPage={perPage}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={({ target: { value } }) => {
                                setPerPageAction(value);
                                setPageAction(0);
                            }}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            labelRowsPerPage=""
                        />
                    </ThemeProvider>
                </ThemeProvider>
            )}
        </>
    );
};

const TableHeader = () => {
    const [isVerifiedStaff] = useAtom(isMbxUserVerifiedStaff);
    const [tableConfig, setTableConfig] = useAtom(tableConfigAtom);
    const { searchTerm } = tableConfig;
    const [, setSelectedServiceId] = useAtom(selectedServiceIdAtom);
    const [openServiceDialog, setOpenServiceDialog] = useAtom(
        openServiceDialogAtom
    );
    const [openServiceCategoriesDialog, setOpenServiceCategoriesDialog] =
        useState(false);

    const themePalette = useTheme().palette;

    const handleClearClick = () => {
        setTableConfig({
            ...tableConfig,
            searchTerm: ""
        });
    };

    const handleCloseServiceDialog = () => {
        setOpenServiceDialog(false);
    };

    return (
        <ThemeProvider theme={tableConfigTheme(themePalette)}>
            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ marginBottom: "15px" }}
            >
                <Stack
                    display="flex"
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap="18px"
                >
                    <Link
                        align="center"
                        underline="hover"
                        onClick={() => setOpenServiceCategoriesDialog(true)}
                    >
                        <Typography variant="body2" color="primary">
                            <b>Manage service categories</b>
                        </Typography>
                    </Link>
                    {openServiceCategoriesDialog && (
                        <AddEditCategoriesDialog
                            open={openServiceCategoriesDialog}
                            onClose={() =>
                                setOpenServiceCategoriesDialog(false)
                            }
                        />
                    )}
                    <FiltersMenu />
                    <TextField
                        id="search"
                        sx={{
                            maxWidth: "200px",
                            "& .MuiInputBase-root": {
                                paddingLeft: "14px !important"
                            }
                        }}
                        value={searchTerm}
                        placeholder="Search"
                        onChange={({ target: { value } }) =>
                            setTableConfig({
                                ...tableConfig,
                                searchTerm: value
                            })
                        }
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            width: "18px",
                                            height: "18px",
                                            color: `${themePalette.helperText.main}`
                                        }}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm &&
                                searchTerm.length > 0 && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            aria-label="clear"
                                            onClick={handleClearClick}
                                        >
                                            <ClearIcon
                                                sx={{
                                                    width: "18px",
                                                    height: "18px"
                                                }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                )
                        }}
                    />
                    {isVerifiedStaff && (
                        <Button
                            textTransform="uppercase"
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon sx={{ color: "white" }} />}
                            onClick={() => {
                                setSelectedServiceId(null);
                                setOpenServiceDialog(true);
                            }}
                        >
                            <b> Add Service</b>
                        </Button>
                    )}
                </Stack>
            </Stack>
        </ThemeProvider>
    );
};

export default function FiltersMenu() {
    const [tableConfig, setTableConfig] = useAtom(tableConfigAtom);
    const { statusFilter, typeFilter, bookingBehaviourFilter } = tableConfig;
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const theme = useTheme();
    return (
        <>
            <TextField
                sx={{ maxWidth: "150px" }}
                value={"Filters"}
                onClick={handleClick}
                fullWidth
                InputProps={{
                    sx: {
                        fontSize: "14px",
                        color: theme.palette.text.main,
                        ...(open && {
                            "& fieldset": {
                                border: `2px solid ${theme.palette.primary.main}`
                            }
                        })
                    },
                    endAdornment: (
                        <InputAdornment position="start">
                            {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        </InputAdornment>
                    ),
                    readOnly: true
                }}
            />
            <ThemeProvider theme={dropDownMenuTheme(theme)}>
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    fullWidth
                    onClick={handleClose}
                    onClose={handleClose}
                    MenuListProps={{
                        "aria-labelledby": "basic-button",
                        sx: {
                            width: anchorEl && anchorEl.offsetWidth,
                            padding: "10px 15px"
                        }
                    }}
                >
                    <FormControl>
                        <FormLabel id="demo-radio-buttons-group-label">
                            <Typography variant="subHeading">Status</Typography>
                        </FormLabel>
                        <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            defaultValue={STATUS_OPTIONS.ACTIVE}
                            name="radio-buttons-group"
                            value={statusFilter}
                            onChange={({ target: { value } }) =>
                                setTableConfig({
                                    ...tableConfig,
                                    statusFilter: value
                                })
                            }
                        >
                            {Object.values(STATUS_OPTIONS).map((option) => (
                                <FormControlLabel
                                    key={option}
                                    value={option}
                                    control={<Radio disableRipple />}
                                    label={STATUS_OPTIONS_LABELS[option]}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                    <FormControl>
                        <FormLabel id="demo-radio-buttons-group-label">
                            <Typography variant="subHeading">
                                SERVICE TYPE
                            </Typography>
                        </FormLabel>
                        <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            defaultValue={TYPE_OPTIONS.ALL}
                            name="radio-buttons-group"
                            value={typeFilter}
                            onChange={({ target: { value } }) =>
                                setTableConfig({
                                    ...tableConfig,
                                    typeFilter: value
                                })
                            }
                        >
                            {Object.values(TYPE_OPTIONS).map((option) => (
                                <FormControlLabel
                                    key={option}
                                    value={option}
                                    control={<Radio disableRipple />}
                                    label={TYPE_OPTIONS_LABELS[option]}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                    <FormControl>
                        <FormLabel id="demo-radio-buttons-group-label">
                            <Typography variant="subHeading">
                                Booking type
                            </Typography>
                        </FormLabel>
                        <RadioGroup
                            aria-labelledby="demo-radio-buttons-group-label"
                            defaultValue={BOOKING_BEHAVIOUR_OPTIONS.ALL}
                            name="radio-buttons-group"
                            value={bookingBehaviourFilter}
                            onChange={({ target: { value } }) =>
                                setTableConfig({
                                    ...tableConfig,
                                    bookingBehaviourFilter: value
                                })
                            }
                        >
                            {Object.values(BOOKING_BEHAVIOUR_OPTIONS).map(
                                (option) => (
                                    <FormControlLabel
                                        key={option}
                                        value={option}
                                        control={<Radio disableRipple />}
                                        label={
                                            BOOKING_BEHAVIOUR_OPTIONS_LABELS[
                                                option
                                            ]
                                        }
                                    />
                                )
                            )}
                        </RadioGroup>
                    </FormControl>
                </Menu>
            </ThemeProvider>
        </>
    );
}
